import axiosInstance, {
  apiGet,
  apiPost,
  apiPut,
  apiDelete,
  type PanLumaRequestConfig,
} from '../../api/axiosInstance';
import type { AccountDeletionPreview } from '../../../generated/api/models/accountDeletionPreview';
import type { DeletionImpact } from '../../../generated/api/models/deletionImpact';
import type { UserCompanyGrantCreate } from '../../../generated/api/models/userCompanyGrantCreate';
import type { UserCompanyRoleUpdate } from '../../../generated/api/models/userCompanyRoleUpdate';
import type { UserDeletionReassignTargetResponse } from '../../../generated/api/models/userDeletionReassignTargetResponse';
import type { UserListFilterInput } from '@/generated/api/models/userListFilterInput';
import type { ListCompaniesApiV1CompaniesGetParams } from '@/generated/api/models/listCompaniesApiV1CompaniesGetParams';
import type { ListGroupsApiV1GroupsGetParams } from '@/generated/api/models/listGroupsApiV1GroupsGetParams';
import {
  User,
  UserCreate,
  UserUpdate,
  UserActivitySummary,
  Company,
  CompanyCreate,
  CompanyUpdate,
  CompanyRole,
  UserCompanyGrant,
  Group,
  GroupCreate,
  GroupUpdate,
  PaginatedResponse,
  HardDeleteRequest,
} from '../types';

export type { AccountDeletionPreview };

const API_BASE = '/api/v1';

/**
 * The filter set a select-all-matching bulk action names (`ui-list-pages.md` §11).
 *
 * IS the server's `UserListFilterInput` — it no longer merely "mirrors" it, so
 * the contract cannot drift: only NARROWING filters travel, and the tenant, the
 * accessible-company bound and the sysadmin flag are server-known facts
 * re-derived per request (sending one is refused, not ignored).
 *
 * Note the generated model is NARROWER than the hand-rolled twin was on three
 * properties — `status` is `UserStatus | null`, `user_type` and
 * `exclude_user_type` are `UserType | null`, where the local copy said bare
 * `string`. That is the request schema's own closed set, so a producer holding a
 * raw `string` (a URL query param) must runtime-narrow it rather than cast: a
 * cast forwards an off-list value into a body typed as the enum and 422s the
 * whole bulk action. See `UserList.tsx`'s `isUserType`.
 */
export type UserBulkFilters = UserListFilterInput;

/**
 * Exactly one scope: the loaded ids, or the whole filter set. Modelled as a union
 * rather than two optional fields so "both" and "neither" — the two the server
 * rejects — cannot be spelled by a caller at all.
 */
export type UserBulkScope =
  | { ids: string[] }
  | { matchAllFiltered: true; filters: UserBulkFilters };

function userBulkBody(scope: UserBulkScope) {
  return 'ids' in scope
    ? { ids: scope.ids }
    : { match_all_filtered: true, filters: scope.filters };
}

// ============== Users ==============

/** Columns `GET /users` will sort by — mirrors the backend whitelist. */
export type ListUsersSortField =
  | 'full_name'
  | 'email'
  | 'status'
  | 'country'
  | 'last_login_at';

export interface ListUsersParams {
  company_id?: string;
  status?: string;
  role?: string;
  search?: string;
  user_type?: string;
  exclude_user_type?: string;
  include_deleted?: boolean;
  /**
   * Server-side sort column, whitelisted by the backend
   * (`SORTABLE_USER_COLUMNS`): full_name | email | status | country |
   * last_login_at. Omit it and the directory keeps its natural ordering — which
   * is what the unpaged user pickers want.
   */
  sort_by?: ListUsersSortField;
  sort_direction?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export const userApi = {
  list: async (params?: ListUsersParams): Promise<PaginatedResponse<User>> => {
    const searchParams = new URLSearchParams();
    if (params?.company_id) searchParams.set('company_id', params.company_id);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.role) searchParams.set('role', params.role);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.user_type) searchParams.set('user_type', params.user_type);
    if (params?.exclude_user_type) searchParams.set('exclude_user_type', params.exclude_user_type);
    if (params?.include_deleted) searchParams.set('include_deleted', 'true');
    if (params?.sort_by) searchParams.set('sort_by', params.sort_by);
    if (params?.sort_direction) searchParams.set('sort_direction', params.sort_direction);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const url = `${API_BASE}/users${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<PaginatedResponse<User>>(url);
  },

  get: async (id: string, includeGroups = false): Promise<{ data: User }> => {
    const url = `${API_BASE}/users/${id}${includeGroups ? '?include_groups=true' : ''}`;
    return apiGet<{ data: User }>(url);
  },

  create: async (data: UserCreate): Promise<{ data: User }> => {
    return apiPost<{ data: User }>(`${API_BASE}/users`, data);
  },

  update: async (id: string, data: UserUpdate): Promise<{ data: User }> => {
    return apiPut<{ data: User }>(`${API_BASE}/users/${id}`, data);
  },

  delete: async (id: string): Promise<void> => {
    return apiDelete(`${API_BASE}/users/${id}`);
  },

  setGroups: async (id: string, groupIds: string[]): Promise<{ data: User }> => {
    return apiPut<{ data: User }>(`${API_BASE}/users/${id}/groups`, { group_ids: groupIds });
  },

  verify: async (id: string): Promise<{ data: User }> => {
    return apiPost<{ data: User }>(`${API_BASE}/users/${id}/verify`, {});
  },

  setPassword: async (id: string, password: string): Promise<{ data: { success: boolean; message: string } }> => {
    return apiPost<{ data: { success: boolean; message: string } }>(`${API_BASE}/users/${id}/set-password`, { password });
  },

  activate: async (id: string): Promise<{ data: User }> => {
    return apiPost<{ data: User }>(`${API_BASE}/users/${id}/activate`, {});
  },

  deactivate: async (id: string): Promise<{ data: User }> => {
    return apiPost<{ data: User }>(`${API_BASE}/users/${id}/deactivate`, {});
  },

  restore: async (id: string): Promise<{ data: User }> => {
    return apiPost<{ data: User }>(`${API_BASE}/users/${id}/restore`, {});
  },

  getDeletionImpact: async (id: string): Promise<DeletionImpact> => {
    // Endpoint returns the shared DeletionImpact model directly (no {data} wrapper).
    return apiGet<DeletionImpact>(`${API_BASE}/users/${id}/deletion-impact`);
  },

  hardDelete: async (id: string, data: HardDeleteRequest): Promise<{ data: Record<string, unknown> }> => {
    return apiPost<{ data: Record<string, unknown> }>(`${API_BASE}/users/${id}/hard-delete`, data);
  },

  /**
   * Pre-assign who inherits a soft-deleted user's records when the 30-day
   * retention sweep hard-deletes them. `null` clears it. The route echoes the
   * stored pair rather than the whole user — callers invalidate the users query
   * and read the row back through it.
   */
  setDeletionReassignTarget: async (
    id: string,
    reassignToUserId: string | null
  ): Promise<UserDeletionReassignTargetResponse> => {
    return apiPut<UserDeletionReassignTargetResponse>(
      `${API_BASE}/users/${id}/deletion-reassign-target`,
      { reassign_to_user_id: reassignToUserId }
    );
  },

  /**
   * Destructive AND escalatable — the loaded ids, or every user matching the
   * filter set. The escalated arm is confirmed through `BulkScopeConfirmDialog`
   * on the surface that offers it (`ui-list-pages.md` §11), never here.
   */
  bulkDelete: async (scope: UserBulkScope): Promise<{ deleted: number }> => {
    return apiPost<{ deleted: number }>(`${API_BASE}/users/bulk-delete`, userBulkBody(scope));
  },

  bulkActivate: async (scope: UserBulkScope): Promise<{ activated: number }> => {
    return apiPost<{ activated: number }>(`${API_BASE}/users/bulk-activate`, userBulkBody(scope));
  },

  bulkDeactivate: async (scope: UserBulkScope): Promise<{ deactivated: number }> => {
    return apiPost<{ deactivated: number }>(`${API_BASE}/users/bulk-deactivate`, userBulkBody(scope));
  },

  getActivitySummary: async (id: string): Promise<{ data: UserActivitySummary }> => {
    return apiGet<{ data: UserActivitySummary }>(`${API_BASE}/users/${id}/activity-summary`);
  },

  // ===== Self-service account deletion =====

  getOwnDeletionPreview: async (): Promise<{ data: AccountDeletionPreview }> => {
    return apiGet<{ data: AccountDeletionPreview }>(`${API_BASE}/users/me/deletion-preview`);
  },

  deleteOwnAccount: async (
    body: { password?: string; confirm_email?: string },
  ): Promise<{ data: { scheduled_purge_at: string; grace_period_days: number } }> => {
    return apiPost<{ data: { scheduled_purge_at: string; grace_period_days: number } }>(
      `${API_BASE}/users/me/delete-account`,
      body,
    );
  },

  /**
   * Cancel a pending account deletion, addressed by the emailed link credential.
   *
   * `skipAuth` because this is a PRE-AUTH screen: the visitor may still hold a
   * stale token from an earlier session, and attaching it makes the interceptor
   * proactively refresh — a failed refresh calls `handleSessionExpired()` and
   * rejects the request BEFORE it reaches the network, so the cancellation is
   * silently dropped and the user is logged out. This request never needed a
   * token: the one-time credential in the body is the whole capability, and the
   * emailed link is the only self-service way to cancel (sessions are blocked
   * while a deletion is pending), so a dropped request costs the account.
   */
  cancelAccountDeletion: async (
    token: string,
  ): Promise<{ data: { status: 'cancelled' | 'already_cancelled'; user_id: string } }> => {
    const response = await axiosInstance.post<{
      data: { status: 'cancelled' | 'already_cancelled'; user_id: string };
    }>(`${API_BASE}/auth/cancel-account-deletion`, { token }, {
      skipAuth: true,
    } as PanLumaRequestConfig);
    return response.data;
  },

  checkHandleAvailable: async (
    handle: string,
    excludeUserId?: string,
  ): Promise<HandleAvailabilityResult> => {
    const params = new URLSearchParams({ handle });
    if (excludeUserId) params.set('user_id', excludeUserId);
    return apiGet<HandleAvailabilityResult>(`${API_BASE}/users/handle-available?${params}`);
  },
};

export interface HandleAvailabilityResult {
  available: boolean;
  reason: 'taken' | 'invalid' | 'reserved' | null;
  message: string | null;
  suggestions: string[];
}

// ============== Companies ==============

/**
 * Query params for `GET /api/v1/companies` — the route `companyApi.list` below
 * builds. The generated envelope is what that operation declares, so it is used
 * directly; it is WIDER than the hand-rolled twin only in admitting an explicit
 * `null` on `status` / `search` / `limit`, which the builder below treats
 * exactly as an omission (every arm is a truthiness test).
 */
export type ListCompaniesParams = ListCompaniesApiV1CompaniesGetParams;

export const companyApi = {
  list: async (params?: ListCompaniesParams): Promise<PaginatedResponse<Company>> => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const url = `${API_BASE}/companies${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<PaginatedResponse<Company>>(url);
  },

  get: async (id: string): Promise<{ data: Company }> => {
    return apiGet<{ data: Company }>(`${API_BASE}/companies/${id}`);
  },

  create: async (data: CompanyCreate): Promise<{ data: Company }> => {
    return apiPost<{ data: Company }>(`${API_BASE}/companies`, data);
  },

  update: async (id: string, data: CompanyUpdate): Promise<{ data: Company }> => {
    return apiPut<{ data: Company }>(`${API_BASE}/companies/${id}`, data);
  },

  delete: async (id: string): Promise<void> => {
    return apiDelete(`${API_BASE}/companies/${id}`);
  },

  /** Companies the caller has access to (via user_company_roles). */
  listMine: async (): Promise<{ data: Company[] }> => {
    return apiGet<{ data: Company[] }>(`${API_BASE}/users/me/companies`);
  },
};

// ============== Per-user company access (admin, multi-company tenants) ==============

export const userCompanyApi = {
  /** List a user's per-company access grants (admin). */
  list: async (userId: string): Promise<{ data: UserCompanyGrant[] }> => {
    return apiGet<{ data: UserCompanyGrant[] }>(`${API_BASE}/users/${userId}/companies`);
  },

  /** Grant a user access to a company with an optional role (defaults to member). */
  add: async (
    userId: string,
    payload: UserCompanyGrantCreate,
  ): Promise<{ data: UserCompanyGrant }> => {
    return apiPost<{ data: UserCompanyGrant }>(`${API_BASE}/users/${userId}/companies`, payload);
  },

  /** Change a user's role in a company. */
  updateRole: async (
    userId: string,
    companyId: string,
    role: CompanyRole,
  ): Promise<{ data: UserCompanyGrant }> => {
    const body: UserCompanyRoleUpdate = { role };
    return apiPut<{ data: UserCompanyGrant }>(
      `${API_BASE}/users/${userId}/companies/${companyId}`,
      body,
    );
  },

  /** Revoke a user's access to a company. */
  remove: async (userId: string, companyId: string): Promise<void> => {
    return apiDelete(`${API_BASE}/users/${userId}/companies/${companyId}`);
  },
};

// ============== Groups ==============

/**
 * Query params for `GET /api/v1/groups`. The route types `type` as a bare
 * `string | null` (it filters, it does not validate), so the generated envelope
 * is wider there than the hand-rolled `'department' | 'office' | 'team' |
 * 'other'` was. Nothing loses a guard: the one caller that passes it,
 * `GroupList`, holds a `GroupType` already, and an off-list value narrows the
 * query to zero rows rather than erroring.
 */
export type ListGroupsParams = ListGroupsApiV1GroupsGetParams;

export interface ListGroupMembersParams {
  limit?: number;
  offset?: number;
}

export const groupApi = {
  list: async (params?: ListGroupsParams): Promise<PaginatedResponse<Group>> => {
    const searchParams = new URLSearchParams();
    if (params?.include_system !== undefined)
      searchParams.set('include_system', String(params.include_system));
    if (params?.type) searchParams.set('type', params.type);
    if (params?.company_id) searchParams.set('company_id', params.company_id);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const url = `${API_BASE}/groups${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<PaginatedResponse<Group>>(url);
  },

  get: async (id: string): Promise<{ data: Group }> => {
    return apiGet<{ data: Group }>(`${API_BASE}/groups/${id}`);
  },

  create: async (data: GroupCreate): Promise<{ data: Group }> => {
    return apiPost<{ data: Group }>(`${API_BASE}/groups`, data);
  },

  update: async (id: string, data: GroupUpdate): Promise<{ data: Group }> => {
    return apiPut<{ data: Group }>(`${API_BASE}/groups/${id}`, data);
  },

  delete: async (id: string): Promise<void> => {
    return apiDelete(`${API_BASE}/groups/${id}`);
  },

  getMembers: async (id: string, params?: ListGroupMembersParams): Promise<PaginatedResponse<User>> => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const url = `${API_BASE}/groups/${id}/members${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<PaginatedResponse<User>>(url);
  },

  addMember: async (groupId: string, userId: string): Promise<{ data: User }> => {
    return apiPost<{ data: User }>(`${API_BASE}/groups/${groupId}/members/${userId}`, {});
  },

  removeMember: async (groupId: string, userId: string): Promise<void> => {
    return apiDelete(`${API_BASE}/groups/${groupId}/members/${userId}`);
  },
};
